import React from "react";
import styled from "styled-components";
import type { IconSvgElement } from "@hugeicons/react";
import {
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  Delete02Icon,
  InformationCircleIcon,
} from "@hugeicons/core-free-icons";

import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";

/**
 * The mark at the top of a dialog that reports an outcome.
 *
 * Every one of these drew its own. The delete confirmation had a 44px circle
 * mixed by hand out of `rgba(220, 53, 69, 0.2)`; the success confirmations had
 * a two-hundred-pixel illustration of a party popper, which made the picture
 * the subject of the dialog rather than the sentence under it; and each of
 * them reached for a different green or red. So four dialogs a click apart
 * announced themselves four different ways.
 *
 * One mark, one size, four tones, all of them straight off the ramp: the tint
 * at 50 behind the glyph at 600, which is what DESIGN.md means by success
 * (#039855) and error (#D92D20). The dialogs pass a tone, not a picture.
 */

export type DialogMarkTone = "success" | "danger" | "warning" | "info";

const TONES: Record<DialogMarkTone, { bg: string; fg: string }> = {
  success: { bg: colors.success[50], fg: colors.success[600] },
  danger: { bg: colors.error[50], fg: colors.error[600] },
  warning: { bg: colors.warning[50], fg: colors.warning[600] },
  info: { bg: colors.primary[15], fg: colors.primary[500] },
};

/** The glyph each tone wears unless a dialog has a better one of its own. */
const DEFAULT_ICON: Record<DialogMarkTone, IconSvgElement> = {
  success: CheckmarkCircle02Icon,
  danger: Delete02Icon,
  warning: AlertCircleIcon,
  info: InformationCircleIcon,
};

const Circle = styled.div<{ $tone: DialogMarkTone }>`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ $tone }) => TONES[$tone].bg};
  color: ${({ $tone }) => TONES[$tone].fg};
`;

export interface DialogMarkProps {
  tone: DialogMarkTone;
  /** Overrides the tone's own glyph, for a dialog that means something more specific. */
  icon?: IconSvgElement;
  className?: string;
}

const DialogMark: React.FC<DialogMarkProps> = ({ tone, icon, className }) => (
  <Circle $tone={tone} className={className}>
    <HugeIcon icon={icon ?? DEFAULT_ICON[tone]} size={22} strokeWidth={1.8} />
  </Circle>
);

export default DialogMark;
